import {
  pgTable,
  uuid,
  text,
  timestamp,
  numeric,
  index,
  pgPolicy,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';

/**
 * Phase 8 / Task 8.5 (§15 GPS breadcrumb trail) — every GPS position captured
 * during a job, the authoritative source for route verification, billing
 * disputes, and compliance. (0,0) is the rejected sentinel (never stored). RLS
 * scoped to tenant. Retention: POSITION points 1yr from completion, event-type
 * points 7yr (§73 — enforced by a scheduled cleanup, not a column).
 */
export const gpsTrailPoints = pgTable(
  'gps_trail_points',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id'),
    ticketId: uuid('ticket_id'),
    driverId: uuid('driver_id'),
    lat: numeric('lat', { precision: 10, scale: 7 }).notNull(),
    lng: numeric('lng', { precision: 10, scale: 7 }).notNull(),
    accuracyFt: numeric('accuracy_ft', { precision: 8, scale: 2 }),
    speedMph: numeric('speed_mph', { precision: 6, scale: 2 }),
    headingDeg: numeric('heading_deg', { precision: 5, scale: 2 }),
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull(),
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    eventType: text('event_type').notNull(),
    eventLabel: text('event_label'),
  },
  (t) => [
    index('gps_trail_points_tenant_job_idx').on(t.tenantId, t.jobId),
    index('gps_trail_points_job_captured_idx').on(t.jobId, t.capturedAt),
    // Migration 0199 also adds the UNIQUE gps_trail_points_point_uk (tenant_id, driver_id,
    // captured_at, event_type, job_id) NULLS NOT DISTINCT: one point is stored once. Not
    // declared here: drizzle 0.45's uniqueIndex() has no NULLS NOT DISTINCT; writeSyncChild
    // relies on it through a bare ON CONFLICT DO NOTHING.
    // Migration 0159 (OPEN-110) — the driver's last GPS point since a shift started.
    index('gps_trail_points_tenant_driver_captured_idx').on(
      t.tenantId,
      t.driverId,
      t.capturedAt.desc(),
    ),
    check(
      'gps_trail_points_event_type_check',
      sql`event_type IN (
        'POSITION','ARRIVED_PICKUP','DEPARTED_PICKUP','ARRIVED_DISPOSAL',
        'DEPARTED_DISPOSAL','JOB_PAUSED','JOB_RESUMED','GAUGE_BEFORE',
        'GAUGE_AFTER','PRINT_EVENT','OFFLINE_START','OFFLINE_END')`,
    ),
    check('gps_trail_points_not_null_island', sql`NOT (lat = 0 AND lng = 0)`),
    pgPolicy('gps_trail_points_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type GpsTrailPoint = typeof gpsTrailPoints.$inferSelect;
export type NewGpsTrailPoint = typeof gpsTrailPoints.$inferInsert;
export const gpsTrailPointSelectSchema = createSelectSchema(gpsTrailPoints);
export const gpsTrailPointInsertSchema = createInsertSchema(gpsTrailPoints);
